/**
 * Pièces de démarche et coffre-fort numérique (CFN).
 *
 * Toute pièce listée dans une modale de démarche (CTVO, DC, DA, DA deal,
 * dépôt de pièces) part au coffre-fort légal à la finalisation du dossier,
 * pour 5 ans. Ce module porte ce qui est partagé entre ces modales : le
 * texte de l'avertissement, la lecture des pièces d'un deal et la traduction
 * des refus du serveur quand on retire une pièce.
 */

export const CFN_PIECES_WARNING_TITLE = 'Ne conservez que les pièces de ce véhicule.';

export const CFN_PIECES_WARNING_BODY =
  'Toutes les pièces listées ci-dessous sont archivées 5 ans dans le coffre-fort numérique légal. ' +
  'Supprimez celles qui ne concernent pas ce dossier (autre véhicule, doublon, document sans rapport) avant de soumettre.';

/**
 * Catégories de `car_deals.photos` archivées au CFN (pipelines A / B / E) —
 * miroir de `CFN_DEAL_PIECE_KEYS` côté backend. Les photos du véhicule
 * (`damage`, `interior`, …) n'en font jamais partie.
 */
export const CFN_DEAL_PIECE_LABELS: Record<string, string> = {
  certificateOfTransfer: 'Certificat de cession',
  barredVehicleRegistrationDocument: 'Carte grise barrée',
  gloveBoxDocuments: 'Documents boîte à gants',
  signedPurchaseDeclaration: "Déclaration d'achat signée",
  signedCessionCertificate: 'Certificat de cession signé',
  signedPurchaseDeclarationDepositSale: "Déclaration d'achat signée (dépôt-vente)",
};

export interface DealProcurementPieceGroup {
  key: string;
  label: string;
  urls: string[];
}

/** Pièces de démarche d'un deal, groupées par catégorie, catégories vides omises. */
export const collectDealProcurementPieces = (
  photos: Record<string, unknown> | null | undefined,
): DealProcurementPieceGroup[] => {
  if (!photos) return [];
  return Object.entries(CFN_DEAL_PIECE_LABELS)
    .map(([key, label]) => {
      const value = photos[key];
      const urls = Array.isArray(value)
        ? value.filter((url): url is string => typeof url === 'string' && url !== '')
        : [];
      return { key, label, urls };
    })
    .filter((group) => group.urls.length > 0);
};

/** Retire une URL d'un groupe de pièces sans muter l'original ; les groupes vidés disparaissent. */
export const removeUrlFromPieceGroups = (
  groups: DealProcurementPieceGroup[],
  url: string,
): DealProcurementPieceGroup[] =>
  groups
    .map((group) => ({ ...group, urls: group.urls.filter((existing) => existing !== url) }))
    .filter((group) => group.urls.length > 0);

interface HttpLikeError {
  response?: { status?: number; data?: { error?: unknown } };
}

const readStatus = (error: unknown): number | undefined => (error as HttpLikeError)?.response?.status;

const readServerMessage = (error: unknown): string | null => {
  const message = (error as HttpLikeError)?.response?.data?.error;
  return typeof message === 'string' && message.trim() ? message : null;
};

/**
 * Le retrait d'une pièce est refusé (409) quand le dossier est déjà parti au
 * coffre : c'est le seul refus « métier », on relaie le message du serveur.
 * 404 = aucune référence en base : rien à retirer, la suppression Storage
 * peut continuer — l'appelant ne doit PAS bloquer sur ce statut.
 */
export const isPieceNotReferenced = (error: unknown): boolean => readStatus(error) === 404;

export const describePieceDetachError = (error: unknown): string => {
  const status = readStatus(error);
  if (status === 409) {
    return (
      readServerMessage(error) ??
      'Ce dossier est déjà parti au coffre-fort numérique : la pièce ne peut plus être retirée.'
    );
  }
  if (status === 400) {
    return readServerMessage(error) ?? 'Pièce invalide : impossible de la retirer du dossier.';
  }
  return 'Impossible de retirer la pièce du dossier pour le moment. Le fichier a été conservé, réessayez plus tard.';
};
